import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import {
  createDocument,
  createFolder,
  deleteDocument,
  deleteFolder,
  docTypeMeta,
  duplicateDocument,
  fetchDocumentsLite,
  fetchFolders,
  renameDocument,
  type DocDocument,
  type DocFolder,
  type DocType,
} from '@/lib/documents';
import { importDocumentFile, IMPORT_ACCEPT } from '@/lib/docImport';
import FormLinkModal from '@/components/feature/FormLinkModal';

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * Espace « Mes documents » du compte utilisateur (équivalent de /dashboard/documents
 * côté commerçant). Les fichiers s'ouvrent dans une page dédiée.
 */
export default function ClientDocumentsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [folders, setFolders] = useState<DocFolder[]>([]);
  const [documents, setDocuments] = useState<DocDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentFolderId, setCurrentFolderId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [showCreateDoc, setShowCreateDoc] = useState(false);
  const [newDocName, setNewDocName] = useState('');
  const [newDocType, setNewDocType] = useState<DocType>('word');
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [renameTarget, setRenameTarget] = useState<DocDocument | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<{ kind: 'folder' | 'doc'; id: number; name: string } | null>(null);
  const [importing, setImporting] = useState(false);
  const importInputRef = useRef<HTMLInputElement | null>(null);
  // Classeur relié à un formulaire public
  const [formLinkDoc, setFormLinkDoc] = useState<DocDocument | null>(null);

  const loadData = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [f, d] = await Promise.all([fetchFolders(user.id), fetchDocumentsLite(user.id)]);
      setFolders(f);
      setDocuments(d);
    } catch {
      setError('Impossible de charger vos documents pour le moment.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    if (!user) navigate('/login-client', { replace: true });
  }, [user, navigate]);

  const breadcrumb = useMemo(() => {
    const chain: DocFolder[] = [];
    let id = currentFolderId;
    const guard = new Set<number>();
    while (id !== null && !guard.has(id)) {
      guard.add(id);
      const folder = folders.find((f) => f.id === id);
      if (!folder) break;
      chain.unshift(folder);
      id = folder.parent_id;
    }
    return chain;
  }, [currentFolderId, folders]);

  const subfolders = useMemo(() => folders.filter((f) => f.parent_id === currentFolderId), [folders, currentFolderId]);

  const currentDocuments = useMemo(() => {
    let list = documents.filter((d) => d.folder_id === currentFolderId);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((d) => d.name.toLowerCase().includes(q));
    }
    return [...list].sort(
      (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
    );
  }, [documents, currentFolderId, search]);

  const docCountInFolder = (folderId: number) => documents.filter((d) => d.folder_id === folderId).length;

  const handleCreateDocument = async () => {
    if (!user || !newDocName.trim()) return;
    try {
      const doc = await createDocument(user.id, newDocName.trim(), newDocType, currentFolderId);
      setShowCreateDoc(false);
      setNewDocName('');
      setNewDocType('word');
      navigate(`/mon-compte/documents/${doc.id}`);
    } catch {
      setError('Impossible de créer ce document.');
    }
  };

  const handleCreateFolder = async () => {
    if (!user || !newFolderName.trim()) return;
    try {
      const folder = await createFolder(user.id, newFolderName.trim(), currentFolderId);
      setFolders((prev) => [...prev, folder]);
      setShowCreateFolder(false);
      setNewFolderName('');
    } catch {
      setError('Impossible de créer ce dossier.');
    }
  };

  const handleRename = async () => {
    if (!renameTarget || !renameValue.trim()) return;
    const trimmed = renameValue.trim();
    try {
      await renameDocument(renameTarget.id, trimmed);
      setDocuments((prev) =>
        prev.map((d) => (d.id === renameTarget.id ? { ...d, name: trimmed, updated_at: new Date().toISOString() } : d)),
      );
    } catch {
      setError('Impossible de renommer ce document.');
    }
    setRenameTarget(null);
    setRenameValue('');
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;
    try {
      if (confirmDelete.kind === 'doc') {
        await deleteDocument(confirmDelete.id);
        setDocuments((prev) => prev.filter((d) => d.id !== confirmDelete.id));
      } else {
        await deleteFolder(confirmDelete.id);
        await loadData();
        if (currentFolderId === confirmDelete.id) setCurrentFolderId(null);
      }
    } catch {
      setError('Suppression impossible.');
    }
    setConfirmDelete(null);
  };

  const handleImport = async (files: FileList | null) => {
    if (!user || !files || files.length === 0) return;
    setImporting(true);
    setError(null);
    const created: DocDocument[] = [];
    const failures: string[] = [];
    for (const file of Array.from(files)) {
      try {
        created.push(await importDocumentFile(user.id, file, currentFolderId));
      } catch (err) {
        failures.push(`${file.name} — ${err instanceof Error ? err.message : 'import impossible'}`);
      }
    }
    if (created.length) setDocuments((prev) => [...created, ...prev]);
    if (failures.length) setError(failures.join(' · '));
    setImporting(false);
    if (importInputRef.current) importInputRef.current.value = '';
  };

  return (
    <div className="min-h-screen bg-background-50">
      <header className="sticky top-0 z-30 bg-background-50/95 backdrop-blur border-b border-background-200/60">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-3 flex items-center gap-3">
          <Link
            to="/mon-compte/overview"
            className="w-9 h-9 rounded-full flex items-center justify-center text-foreground-500 hover:bg-background-100 cursor-pointer no-underline"
            title="Retour au compte"
          >
            <i className="ri-arrow-left-line"></i>
          </Link>
          <div className="flex-1 min-w-0">
            <h1 className="text-base md:text-lg font-bold font-heading text-foreground-950 flex items-center gap-2">
              <i className="ri-file-text-line text-primary-500"></i>
              Mes documents
            </h1>
            <p className="text-[11px] text-foreground-400">
              {documents.length} document{documents.length !== 1 ? 's' : ''} · {folders.length} dossier
              {folders.length !== 1 ? 's' : ''}
            </p>
          </div>
          <button
            onClick={() => setShowCreateDoc(true)}
            className="hidden sm:flex items-center gap-2 px-4 py-2.5 rounded-full bg-primary-500 text-background-50 text-xs font-semibold whitespace-nowrap hover:bg-primary-600 cursor-pointer transition-colors"
          >
            <i className="ri-file-add-line"></i>
            Nouveau document
          </button>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-4 md:px-6 py-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <nav className="flex items-center gap-1 text-sm flex-wrap" aria-label="Fil d'Ariane">
            <button
              onClick={() => setCurrentFolderId(null)}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full cursor-pointer whitespace-nowrap transition-colors ${
                currentFolderId === null ? 'bg-primary-50 text-primary-700 font-medium' : 'text-foreground-600 hover:bg-background-100'
              }`}
            >
              <i className="ri-home-5-line"></i>
              Mes documents
            </button>
            {breadcrumb.map((folder) => (
              <span key={folder.id} className="flex items-center gap-1">
                <i className="ri-arrow-right-s-line text-foreground-300"></i>
                <button
                  onClick={() => setCurrentFolderId(folder.id)}
                  className="px-2.5 py-1.5 rounded-full cursor-pointer text-foreground-600 hover:bg-background-100 whitespace-nowrap transition-colors"
                >
                  {folder.name}
                </button>
              </span>
            ))}
          </nav>

          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher…"
                className="w-full sm:w-48 pl-9 pr-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300"
              />
            </div>
            <input
              ref={importInputRef}
              type="file"
              multiple
              accept={IMPORT_ACCEPT}
              className="hidden"
              onChange={(e) => void handleImport(e.target.files)}
            />
            <button
              onClick={() => importInputRef.current?.click()}
              disabled={importing}
              className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-background-100 text-foreground-700 text-xs font-medium hover:bg-background-200/70 disabled:opacity-60 cursor-pointer whitespace-nowrap transition-colors"
            >
              <i className={importing ? 'ri-loader-4-line animate-spin' : 'ri-upload-cloud-2-line'}></i>
              Importer
            </button>
            <button
              onClick={() => setShowCreateFolder(true)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-background-100 text-foreground-700 text-xs font-medium hover:bg-background-200/70 cursor-pointer whitespace-nowrap transition-colors"
            >
              <i className="ri-folder-add-line"></i>
              Dossier
            </button>
            <button
              onClick={() => setShowCreateDoc(true)}
              className="sm:hidden flex items-center gap-1.5 px-3 py-2 rounded-full bg-primary-500 text-background-50 text-xs font-semibold cursor-pointer whitespace-nowrap"
            >
              <i className="ri-file-add-line"></i>
              Document
            </button>
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 px-4 py-2.5 bg-red-50 text-red-700 text-xs rounded-lg mb-4">
            <i className="ri-error-warning-line"></i>
            <span className="flex-1">{error}</span>
            <button onClick={() => setError(null)} className="cursor-pointer"><i className="ri-close-line"></i></button>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
          </div>
        ) : (
          <>
            {subfolders.length > 0 && (
              <div className="mb-5">
                <h2 className="text-[10px] font-semibold uppercase tracking-wider text-foreground-400 mb-2">Dossiers</h2>
                <div className="rounded-lg border border-background-200/70 bg-background-50 divide-y divide-background-200/60">
                  {subfolders.map((folder) => (
                    <div key={folder.id} className="group flex items-center gap-2.5 px-3 py-2 hover:bg-background-100/70 transition-colors">
                      <span className="w-7 h-7 rounded-md bg-accent-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-folder-3-fill text-accent-600 text-sm"></i>
                      </span>
                      <button
                        onClick={() => setCurrentFolderId(folder.id)}
                        className="flex-1 min-w-0 text-left cursor-pointer"
                        title={folder.name}
                      >
                        <p className="text-xs font-medium text-foreground-800 truncate">{folder.name}</p>
                        <p className="text-[10px] text-foreground-400">{docCountInFolder(folder.id)} document(s)</p>
                      </button>
                      <button
                        onClick={() => setConfirmDelete({ kind: 'folder', id: folder.id, name: folder.name })}
                        className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-300 hover:text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100 cursor-pointer transition-all"
                        title="Supprimer le dossier"
                      >
                        <i className="ri-delete-bin-line text-xs"></i>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <h2 className="text-[10px] font-semibold uppercase tracking-wider text-foreground-400 mb-2">Documents</h2>
            {currentDocuments.length === 0 ? (
              <div className="flex flex-col items-center py-16 bg-background-50 border border-background-200/70 rounded-xl text-center px-6">
                <i className="ri-file-list-3-line text-4xl text-foreground-300 mb-3"></i>
                <p className="text-sm font-medium text-foreground-700">{search ? 'Aucun résultat' : 'Aucun document ici'}</p>
                <p className="text-xs text-foreground-400 mt-1">
                  Créez un document texte, une feuille de calcul ou une présentation.
                </p>
                <button
                  onClick={() => setShowCreateDoc(true)}
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-xs font-semibold hover:bg-primary-600 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-file-add-line"></i>
                  Nouveau document
                </button>
              </div>
            ) : (
              <div className="rounded-lg border border-background-200/70 bg-background-50 divide-y divide-background-200/60">
                {currentDocuments.map((doc) => {
                  const meta = docTypeMeta[doc.doc_type] || docTypeMeta.word;
                  return (
                    <div key={doc.id} className="group flex items-center gap-2.5 px-3 py-2 hover:bg-background-100/70 transition-colors">
                      <span className="w-7 h-7 rounded-md bg-white border border-background-200/70 flex items-center justify-center flex-shrink-0">
                        <i className={`${meta.icon} text-sm ${meta.iconClass}`}></i>
                      </span>
                      <button
                        onClick={() => navigate(`/mon-compte/documents/${doc.id}`)}
                        className="flex-1 min-w-0 text-left cursor-pointer"
                        title={doc.name}
                      >
                        <p className="text-xs font-medium text-foreground-800 truncate">{doc.name}</p>
                        <p className="text-[10px] text-foreground-400 truncate">
                          {meta.label} · {formatDate(doc.updated_at)}
                        </p>
                      </button>
                      <div className="flex items-center gap-0.5 opacity-60 group-hover:opacity-100 transition-opacity">
                        {doc.doc_type === 'excel' && (
                          <button
                            onClick={() => setFormLinkDoc(doc)}
                            className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-400 hover:text-secondary-700 hover:bg-secondary-100 cursor-pointer"
                            title="Lier un formulaire au classeur"
                          >
                            <i className="ri-survey-line text-xs"></i>
                          </button>
                        )}
                        <button
                          onClick={() => { setRenameTarget(doc); setRenameValue(doc.name); }}
                          className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-400 hover:text-foreground-800 hover:bg-background-100 cursor-pointer"
                          title="Renommer"
                        >
                          <i className="ri-edit-line text-xs"></i>
                        </button>
                        <button
                          onClick={() => { void duplicateDocument(doc).then((copy) => setDocuments((prev) => [copy, ...prev])); }}
                          className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-400 hover:text-accent-600 hover:bg-accent-50 cursor-pointer"
                          title="Dupliquer"
                        >
                          <i className="ri-file-copy-line text-xs"></i>
                        </button>
                        <button
                          onClick={() => setConfirmDelete({ kind: 'doc', id: doc.id, name: doc.name })}
                          className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-400 hover:text-red-500 hover:bg-red-50 cursor-pointer"
                          title="Supprimer"
                        >
                          <i className="ri-delete-bin-line text-xs"></i>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </main>

      {/* Création de document */}
      {showCreateDoc && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowCreateDoc(false)}></div>
          <div className="relative w-full max-w-md bg-background-50 rounded-xl p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-base font-semibold font-heading text-foreground-950">Nouveau document</h3>
              <button onClick={() => setShowCreateDoc(false)} className="w-8 h-8 rounded-full text-foreground-500 hover:bg-background-100 flex items-center justify-center cursor-pointer">
                <i className="ri-close-line"></i>
              </button>
            </div>
            <p className="text-xs text-foreground-500 mb-2">Type de document</p>
            <div className="grid grid-cols-3 gap-2 mb-4">
              {(['word', 'excel', 'powerpoint'] as DocType[]).map((t) => {
                const meta = docTypeMeta[t];
                return (
                  <button
                    key={t}
                    onClick={() => setNewDocType(t)}
                    className={`flex flex-col items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${
                      newDocType === t ? 'border-primary-400 bg-primary-50' : 'border-background-200/70 bg-white hover:bg-background-50'
                    }`}
                  >
                    <i className={`${meta.icon} text-2xl ${meta.iconClass}`}></i>
                    <span className="text-[11px] font-medium text-foreground-700">{meta.label}</span>
                  </button>
                );
              })}
            </div>
            <label className="block text-xs text-foreground-500 mb-1.5">Nom du document</label>
            <input
              value={newDocName}
              onChange={(e) => setNewDocName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void handleCreateDocument(); }}
              placeholder="Document sans titre"
              autoFocus
              className="w-full px-4 py-2.5 bg-white border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300"
            />
            <div className="flex items-center justify-end gap-2 mt-5">
              <button onClick={() => setShowCreateDoc(false)} className="px-4 py-2 rounded-full text-xs text-foreground-600 hover:bg-background-100 cursor-pointer">Annuler</button>
              <button
                onClick={() => void handleCreateDocument()}
                disabled={!newDocName.trim()}
                className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-xs font-semibold hover:bg-primary-600 disabled:opacity-50 cursor-pointer whitespace-nowrap"
              >
                Créer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Création de dossier */}
      {showCreateFolder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowCreateFolder(false)}></div>
          <div className="relative w-full max-w-sm bg-background-50 rounded-xl p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-base font-semibold font-heading text-foreground-950">Nouveau dossier</h3>
              <button onClick={() => setShowCreateFolder(false)} className="w-8 h-8 rounded-full text-foreground-500 hover:bg-background-100 flex items-center justify-center cursor-pointer">
                <i className="ri-close-line"></i>
              </button>
            </div>
            <label className="block text-xs text-foreground-500 mb-1.5">Nom du dossier</label>
            <input
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void handleCreateFolder(); }}
              placeholder="Mon dossier"
              autoFocus
              className="w-full px-4 py-2.5 bg-white border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300"
            />
            <div className="flex items-center justify-end gap-2 mt-5">
              <button onClick={() => setShowCreateFolder(false)} className="px-4 py-2 rounded-full text-xs text-foreground-600 hover:bg-background-100 cursor-pointer">Annuler</button>
              <button
                onClick={() => void handleCreateFolder()}
                disabled={!newFolderName.trim()}
                className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-xs font-semibold hover:bg-primary-600 disabled:opacity-50 cursor-pointer whitespace-nowrap"
              >
                Créer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Renommage */}
      {renameTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setRenameTarget(null)}></div>
          <div className="relative w-full max-w-sm bg-background-50 rounded-xl p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-base font-semibold font-heading text-foreground-950">Renommer</h3>
              <button onClick={() => setRenameTarget(null)} className="w-8 h-8 rounded-full text-foreground-500 hover:bg-background-100 flex items-center justify-center cursor-pointer">
                <i className="ri-close-line"></i>
              </button>
            </div>
            <input
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void handleRename(); }}
              autoFocus
              className="w-full px-4 py-2.5 bg-white border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300"
            />
            <div className="flex items-center justify-end gap-2 mt-5">
              <button onClick={() => setRenameTarget(null)} className="px-4 py-2 rounded-full text-xs text-foreground-600 hover:bg-background-100 cursor-pointer">Annuler</button>
              <button onClick={() => void handleRename()} className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-xs font-semibold hover:bg-primary-600 cursor-pointer whitespace-nowrap">Renommer</button>
            </div>
          </div>
        </div>
      )}

      {/* Suppression */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setConfirmDelete(null)}></div>
          <div className="relative w-full max-w-sm bg-background-50 rounded-xl p-6">
            <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mb-4">
              <i className="ri-delete-bin-line text-xl text-red-500"></i>
            </div>
            <h3 className="text-base font-semibold font-heading text-foreground-950">
              Supprimer {confirmDelete.kind === 'folder' ? 'le dossier' : 'le document'} ?
            </h3>
            <p className="text-xs text-foreground-500 mt-1.5">{confirmDelete.name}</p>
            <div className="flex items-center justify-end gap-2 mt-6">
              <button onClick={() => setConfirmDelete(null)} className="px-4 py-2 rounded-full text-xs text-foreground-600 hover:bg-background-100 cursor-pointer">Annuler</button>
              <button onClick={() => void handleConfirmDelete()} className="px-5 py-2 rounded-full bg-red-500 text-white text-xs font-semibold hover:bg-red-600 cursor-pointer whitespace-nowrap">Supprimer</button>
            </div>
          </div>
        </div>
      )}

      {/* Formulaire relié au classeur (réponses écrites dans les lignes) */}
      {formLinkDoc && user && (
        <FormLinkModal
          userId={user.id}
          document={formLinkDoc}
          onClose={() => setFormLinkDoc(null)}
        />
      )}
    </div>
  );
}